import React, { useState, useEffect } from 'react';
import { useFocusMusic } from '@/hooks/useFocusMusic';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Music2, Play, Pause, Square, Volume2, Star, ChevronDown, ChevronUp, Download, Minimize2, Clock, Heart } from 'lucide-react';

const FocusMusicWidget: React.FC = () => {
  const { tracks, currentTrack, isPlaying, volume, setTrack, toggle, stop, setVolume, play, cacheAllTracks, isCaching, cacheProgress } = useFocusMusic();
  const [collapsed, setCollapsed] = useState(() => {
    const saved = localStorage.getItem('focusMusicWidget.collapsed');
    return saved ? JSON.parse(saved) : false;
  });
  const [minimized, setMinimized] = useState(() => {
    const saved = localStorage.getItem('focusMusicWidget.minimized');
    return saved ? JSON.parse(saved) : false;
  });

  // Group tracks by category
  const tracksByCategory = tracks.reduce((acc, track) => {
    if (!acc[track.category]) {
      acc[track.category] = [];
    }
    acc[track.category].push(track);
    return acc;
  }, {} as Record<string, typeof tracks>);
  
  useEffect(() => {
    localStorage.setItem('focusMusicWidget.collapsed', JSON.stringify(collapsed));
  }, [collapsed]);

  useEffect(() => {
    localStorage.setItem('focusMusicWidget.minimized', JSON.stringify(minimized));
  }, [minimized]);
  
  const handleToggleCollapse = () => {
    setCollapsed(!collapsed);
  };

  const handleToggleMinimize = () => {
    setMinimized(!minimized);
  };

  return (
    <div className={`fixed bottom-4 right-4 z-50 rounded-xl border bg-card shadow-lg transition-all duration-300 ${
      minimized ? 'w-16 h-16' : 'w-[320px]'
    }`}>
      {minimized ? (
        <div className="flex items-center justify-center h-full">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleToggleMinimize}
            className="h-12 w-12 p-0 hover:bg-accent"
          >
            <Music2 className={`h-5 w-5 ${isPlaying ? 'text-primary animate-pulse' : 'text-muted-foreground'}`} />
          </Button>
        </div>
      ) : (
        <>
          <div className="flex items-center justify-between p-3 border-b">
            <div className="flex items-center gap-2">
              <Music2 className="h-4 w-4 text-primary" />
              <span className="text-sm font-medium">Focus Music</span>
              {currentTrack && (
                <Badge variant="secondary" className="text-xs">
                  Playing
                </Badge>
              )}
            </div>
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleToggleMinimize}
                className="h-8 w-8 p-0"
                title="Minimize"
              >
                <Minimize2 className="h-3 w-3" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleToggleCollapse}
                className="h-8 w-8 p-0"
                title={collapsed ? "Expand" : "Collapse"}
              >
                {collapsed ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              </Button>
            </div>
          </div>

          {!collapsed && (
            <div className="p-3 space-y-3">
              <div className="space-y-2">
                <label className="text-xs text-muted-foreground">Music type</label>
                <Select
                  value={currentTrack?.id || ''}
                  onValueChange={async (val) => {
                    await setTrack(val);
                    // If user was playing, continue playing newly selected track
                    if (isPlaying) await play(val);
                  }}
                >
                  <SelectTrigger className="h-9" aria-label="Select focus music track">
                    <SelectValue placeholder="Choose focus music" />
                  </SelectTrigger>
                  <SelectContent className="max-h-[300px]">
                    {Object.entries(tracksByCategory).map(([category, categoryTracks]) => (
                      <div key={category}>
                        <div className="px-2 py-1.5 text-xs font-medium text-muted-foreground border-b">
                          {category}
                        </div>
                        {categoryTracks.map((t) => (
                          <SelectItem key={t.id} value={t.id} className="pl-4">
                            <div className="flex items-center justify-between w-full">
                              <div className="flex items-center gap-2">
                                <span className="font-medium">{t.name}</span>
                                {t.isBest && (
                                  <Star className="h-3 w-3 text-primary fill-current" />
                                )}
                              </div>
                              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                                {t.duration && (
                                  <div className="flex items-center gap-1">
                                    <Clock className="h-3 w-3" />
                                    <span>{t.duration}</span>
                                  </div>
                                )}
                              </div>
                            </div>
                            {t.description && (
                              <div className="text-xs text-muted-foreground mt-0.5">
                                {t.description}
                              </div>
                            )}
                          </SelectItem>
                        ))}
                      </div>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Current track info */}
              {currentTrack && (
                <div className="p-2 rounded-lg bg-muted/50 space-y-1">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">{currentTrack.name}</span>
                    {currentTrack.isBest && (
                      <Badge variant="secondary" className="text-xs gap-1">
                        <Star className="h-3 w-3 fill-current" />
                        Best
                      </Badge>
                    )}
                  </div>
                  {currentTrack.description && (
                    <p className="text-xs text-muted-foreground">{currentTrack.description}</p>
                  )}
                  <div className="flex items-center gap-3 text-xs text-muted-foreground">
                    <span className="capitalize">{currentTrack.category}</span>
                    {currentTrack.duration && (
                      <div className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        <span>{currentTrack.duration}</span>
                      </div>
                    )}
                    {currentTrack.mood && (
                      <span className="capitalize">• {currentTrack.mood}</span>
                    )}
                  </div>
                </div>
              )}

              <div className="flex items-center gap-2">
                <Button size="sm" onClick={() => toggle()} className="gap-2">
                  {isPlaying ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                  {isPlaying ? 'Pause' : 'Play'}
                </Button>
                <Button size="sm" variant="outline" onClick={stop} className="gap-2">
                  <Square className="h-4 w-4" /> Stop
                </Button>
                <Button size="sm" variant="outline" onClick={cacheAllTracks} disabled={isCaching} className="gap-2">
                  <Download className="h-4 w-4" /> {isCaching ? `Caching ${Math.round(cacheProgress * 100)}%` : 'Cache All'}
                </Button>
              </div>

              {/* Cache progress indicator */}
              {isCaching && (
                <div className="space-y-1">
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <span>Caching tracks for offline use...</span>
                    <span>{Math.round(cacheProgress * 100)}%</span>
                  </div>
                  <div className="w-full bg-secondary/20 rounded-full h-1.5">
                    <div 
                      className="bg-primary h-1.5 rounded-full transition-all duration-300" 
                      style={{ width: `${cacheProgress * 100}%` }}
                    />
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Volume2 className="h-3 w-3" />
                  <span>Volume: {Math.round(volume * 100)}%</span>
                </div>
                <Slider
                  value={[volume]}
                  onValueChange={(v) => setVolume(v[0])}
                  min={0}
                  max={1}
                  step={0.05}
                  className="w-full"
                  aria-label="Volume control"
                />
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default FocusMusicWidget;